import React, { useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import { SFDCIntegration } from "../types";
import { useSfdcAuthentication } from "../hooks/useSfdcAuthentication";
import { INTEGRATIONS_DROPDOWN_TEXTS } from "../consts";
import { useSalesforceIntegrationDetailsContext } from "../contexts/useSalesforceIntegrationDetailsContext";

export const SalesforceIntegrationsDropdown: React.FC = () => {
  const { integrations, integration: selected } =
    useSalesforceIntegrationDetailsContext();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);

  const existingIntegrationNames = useMemo(
    () => integrations.map((i) => i.name),
    [integrations]
  );
  const hasProductionIntegration = useMemo(
    () => integrations.some((i) => i.environment === "PRODUCTION"),
    [integrations]
  );

  // Authentication hooks
  const authenticateProduction = useSfdcAuthentication({
    isProdEnv: true,
    orgNames: existingIntegrationNames,
  });

  const authenticateSandbox = useSfdcAuthentication({
    isProdEnv: false,
    orgNames: existingIntegrationNames,
  });

  // Handlers
  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleSelectIntegration = useCallback(
    (integration: SFDCIntegration) => {
      if (!integration?.id) {
        return;
      }

      handleClose();
      if (!selected || selected.id !== integration.id) {
        navigate(`/integrations/salesforce/${integration.id}`);
      }
    },
    [selected, navigate, handleClose]
  );

  const handleAddProductionIntegration = useCallback(() => {
    handleClose();
    authenticateProduction();
  }, [handleClose, authenticateProduction]);

  const handleAddSandboxIntegration = useCallback(() => {
    handleClose();
    authenticateSandbox();
  }, [handleClose, authenticateSandbox]);

  // Internal component for integration menu items
  const IntegrationMenuItem: React.FC<{
    integration: SFDCIntegration;
    isSelected: boolean;
  }> = React.memo(({ integration, isSelected }) => (
    <MenuItem
      data-testid={`salesforce-org-menuitem-${integration.id}`}
      onClick={() => handleSelectIntegration(integration)}
      selected={isSelected}
      sx={{ pl: 1 }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{ width: "100%" }}
      >
        <Stack
          sx={{
            width: 20,
          }}
          alignItems="center"
          justifyContent="center"
        >
          {isSelected && (
            <CheckIcon fontSize="small" sx={{ color: "primary.main" }} />
          )}
        </Stack>
        <Typography
          variant="body2"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {integration.name}
        </Typography>
      </Stack>
    </MenuItem>
  ));

  // Internal component for add integration menu items
  const AddIntegrationMenuItem: React.FC<{
    label: string;
    onClick: () => void;
    disabled?: boolean;
    tooltipText?: string;
  }> = React.memo(({ label, onClick, disabled = false, tooltipText }) => {
    const menuItem = (
      <MenuItem
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        sx={{ pl: 1 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Stack
            sx={{
              width: 20,
            }}
            alignItems="center"
            justifyContent="center"
          >
            <AddIcon
              fontSize="small"
              sx={{ color: disabled ? "text.disabled" : "text.secondary" }}
            />
          </Stack>
          <Typography variant="body2">{label}</Typography>
        </Stack>
      </MenuItem>
    );

    if (tooltipText && disabled) {
      return (
        <Tooltip title={tooltipText} placement="right">
          <span>{menuItem}</span>
        </Tooltip>
      );
    }

    return menuItem;
  });

  return (
    <>
      <Tooltip title={INTEGRATIONS_DROPDOWN_TEXTS.tooltips.switchIntegration}>
        <Button
          data-testid="salesforce-orgs-dropdown-btn"
          ref={anchorRef}
          onClick={handleToggle}
          variant="outlined"
          color="secondary"
          size="small"
          sx={{
            minWidth: 32,
            width: 32,
            height: 32,
            borderRadius: "999px",
          }}
        >
          <KeyboardArrowDownIcon
            fontSize="small"
            sx={{
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s",
            }}
          />
        </Button>
      </Tooltip>

      <Menu
        open={open}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: { maxWidth: 400 },
          },
        }}
      >
        {/* Integration items */}
        {integrations.length > 0 ? (
          integrations.map((integration) => (
            <IntegrationMenuItem
              key={integration.id}
              integration={integration}
              isSelected={selected?.id === integration.id}
            />
          ))
        ) : (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              No integrations connected
            </Typography>
          </MenuItem>
        )}

        {/* Divider */}
        {integrations.length > 0 && <Divider sx={{ my: 0.5 }} />}

        {/* Add integration items */}
        <AddIntegrationMenuItem
          label="Add Production Integration"
          onClick={handleAddProductionIntegration}
          disabled={hasProductionIntegration}
          tooltipText={
            hasProductionIntegration
              ? INTEGRATIONS_DROPDOWN_TEXTS.tooltips.productionLimit
              : undefined
          }
        />

        <AddIntegrationMenuItem
          label={INTEGRATIONS_DROPDOWN_TEXTS.labels.addSandbox}
          onClick={handleAddSandboxIntegration}
        />
      </Menu>
    </>
  );
};
